import React, { useState, useRef, useEffect } from 'react';
import { Box, Container, Grid, Typography, Link, IconButton, Divider, Popover, Button } from '@mui/material';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import FacebookIcon from '@mui/icons-material/Facebook';
import YouTubeIcon from '@mui/icons-material/YouTube';
import InstagramIcon from '@mui/icons-material/Instagram';
import XIcon from '@mui/icons-material/X';
import LanguageIcon from '@mui/icons-material/Language';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import EmailIcon from '@mui/icons-material/Email';
import { Link as RouterLink } from 'react-router-dom';

const DEV_LINKS = [
  { icon: <LinkedInIcon />, label: 'LinkedIn', href: 'https://www.linkedin.com/in/kennethkilleen/' },
  { icon: <EmailIcon />, label: 'Email', href: 'mailto:ken@improvisedmusic.ie?subject=ShowRunnr%20Inquiry' },
];

const SOCIAL_LINKS = [
  { icon: <FacebookIcon />, label: 'Facebook', href: 'https://www.facebook.com/imcmusic/' },
  { icon: <XIcon />, label: 'X / Twitter', href: 'https://x.com/ImprovisedMusic' },
  { icon: <InstagramIcon />, label: 'Instagram', href: 'https://www.instagram.com/improvisedmusic/' },
  { icon: <YouTubeIcon />, label: 'YouTube', href: 'https://www.youtube.com/user/ImprovisedMusicCo' },
  { icon: <LanguageIcon />, label: 'Website', href: 'https://www.improvisedmusic.ie' },
];

const PublicFooter: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [installPrompt, setInstallPrompt] = useState<any>(null);

  useEffect(() => {
    const onReady = () => setInstallPrompt((window as any)._pwaInstallPrompt ?? null);
    if ((window as any)._pwaInstallPrompt) {
      setInstallPrompt((window as any)._pwaInstallPrompt);
    }
    window.addEventListener('pwainstallready', onReady);
    window.addEventListener('appinstalled', () => setInstallPrompt(null));
    return () => {
      window.removeEventListener('pwainstallready', onReady);
    };
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setAnchorEl(e.currentTarget);
  };

  const handleMouseLeave = () => {
    closeTimer.current = setTimeout(() => setAnchorEl(null), 200);
  };

  const handlePopoverEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  };

  const open = Boolean(anchorEl);

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: 'primary.main',
        color: 'white',
        py: 6,
        mt: 'auto',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>

          {/* Branding */}
          <Grid item xs={12} sm={4}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              ShowRunnr
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 0.5 }}>
              an initiative of
            </Typography>
            <Link
              href="https://www.improvisedmusic.ie"
              target="_blank"
              rel="noopener noreferrer"
              color="inherit"
              underline="hover"
              sx={{ fontWeight: 600 }}
            >
              Improvised Music Company
            </Link>
            {installPrompt && (
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PhoneIphoneIcon />}
                  onClick={handleInstall}
                  sx={{
                    color: 'white',
                    borderColor: 'rgba(255,255,255,0.4)',
                    '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.08)' },
                  }}
                >
                  Install ShowRunnr
                </Button>
              </Box>
            )}
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} sm={4}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Quick Links
            </Typography>
            {[
              { label: 'Artists', to: '/bands' },
              { label: 'Strands', to: '/publicstrands' },
              ].map(({ label, to }) => (
              <Link
                key={to}
                component={RouterLink}
                to={to}
                color="inherit"
                underline="hover"
                display="block"
                sx={{ mb: 0.5, color: 'rgba(255,255,255,0.85)' }}
              >
                {label}
              </Link>
            ))}
            <Link
              href="https://www.improvisedmusic.ie"
              target="_blank"
              rel="noopener noreferrer"
              color="inherit"
              underline="hover"
              display="block"
              sx={{ mb: 0.5, color: 'rgba(255,255,255,0.85)' }}
            >
              IMC Website
            </Link>
          </Grid>

          {/* Social */}
          <Grid item xs={12} sm={4}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Connect With IMC
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
              {SOCIAL_LINKS.map(({ icon, label, href }) => (
                <IconButton
                  key={label}
                  aria-label={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: 'white',
                    '&:hover': { color: 'secondary.main', transform: 'translateY(-2px)' },
                    transition: 'all 0.2s ease',
                  }}
                >
                  {icon}
                </IconButton>
              ))}
            </Box>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ color: 'rgba(255,255,255,0.85)' }}>
              Connect With ShowRunnr Dev
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {DEV_LINKS.map(({ icon, label, href }) => (
                <IconButton
                  key={label}
                  aria-label={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: 'white',
                    '&:hover': { color: 'secondary.main', transform: 'translateY(-2px)' },
                    transition: 'all 0.2s ease',
                  }}
                >
                  {icon}
                </IconButton>
              ))}
            </Box>
          </Grid>

        </Grid>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)', my: 4 }} />

        <Typography variant="body2" align="center" sx={{ color: 'rgba(255,255,255,0.6)' }}>
          Software © {new Date().getFullYear()} Improvised Music Company. Powered by ShowRunnr. All rights reserved.
        </Typography>
        <Typography variant="body2" align="center" sx={{ color: 'rgba(255,255,255,0.6)', mt: 0.5 }}>
          <Box
            component="span"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            sx={{
              borderBottom: '1px dashed rgba(255,255,255,0.4)',
              cursor: 'default',
              '&:hover': { color: 'rgba(255,255,255,0.9)' },
              transition: 'color 0.2s ease',
            }}
          >
            Available for licensing by jazz organisations and music development bodies worldwide.
          </Box>
          {' '}Licensing enquiries:{' '}
          <Link href="mailto:ken@improvisedmusic.ie?subject=ShowRunnr%20Inquiry" color="inherit" underline="hover">
            ken[at]improvisedmusic[.]ie
          </Link>
        </Typography>

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          disableRestoreFocus
          sx={{ pointerEvents: 'none' }}
          slotProps={{
            paper: {
              onMouseEnter: handlePopoverEnter,
              onMouseLeave: handleMouseLeave,
              sx: {
                pointerEvents: 'auto',
                maxWidth: 380,
                p: 2.5,
                backgroundColor: 'primary.dark',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.15)',
              },
            },
          }}
        >
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            ShowRunnr — Licensing
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mb: 2 }}>
            This platform is built on proprietary software developed by Improvised Music Company. If your organisation
            is interested in deploying a customised version for your own region or repertoire, we offer licensing
            arrangements tailored to scale.
          </Typography>
          <Link
            href="mailto:ken@improvisedmusic.ie?subject=ShowRunnr%20Licensing%20Enquiry"
            sx={{
              color: 'secondary.light',
              fontWeight: 600,
              fontSize: '0.85rem',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Get in touch to discuss →
          </Link>
        </Popover>
      </Container>
    </Box>
  );
};

export default PublicFooter;
